import React, { useEffect, useRef, useState } from 'react';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import api from '../../redux/features/auth/axiosAuthHelper';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import SignInDialog from '../../components/common/SignInDialog';

const filterData = (data: any, filterPaths: string[]) => {
  if (filterPaths.length === 0) {
    return data;
  }
  const filteredData: any = Object.fromEntries(
    Object.entries(data).filter(([key]) => key !== 'paths')
  );
  const paths: any = Object.fromEntries(
    Object.entries(data.paths).filter(([key]) =>
      filterPaths.some((filterPath) => key.startsWith(filterPath))
    )
  );
  filteredData.paths = paths;
  return filteredData;
};

const fetchSwaggerJSON = async (
  urlPath: string,
  accessToken: string | null,
  filterPaths: string[]
) => {
  try {
    const response = await api.get(urlPath, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return filterData(response.data, filterPaths);
  } catch (error) {
    console.error('Error fetching swagger:', error);
    return null;
  }
};

interface SwaggerProps {}

const SwaggerPage: React.FC<SwaggerProps> = ({}) => {
  const [swaggerData, setSwaggerData] = useState(null);
  const authData = useSelector((state: RootState) => state.auth);
  const [swaggerUI, setSwaggerUI] = useState<any>(null);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (swaggerUI) {
      const originalAuthorize = swaggerUI.authActions.authorize;

      swaggerUI.authActions.authorize = (authObject: any) => {
        const { value } = authObject.BearerAuth;
        tokenRef.current = value;
        // swaggerUI.preauthorizeApiKey('bearer', value);

        setTimeout(() => {
          const closeButton = document.querySelector(
            '.btn.modal-btn.auth.btn-done.button'
          );
          if (closeButton) {
            (closeButton as HTMLButtonElement).click();
          }
        }, 500);
      };

      return () => {
        swaggerUI.authActions.authorize = originalAuthorize;
      };
    }
  }, [swaggerUI]);

  useEffect(() => {
    const fetchData = async () => {
      const djangoData = await fetchSwaggerJSON(
        'swagger.json',
        authData.access,
        ['/api/auth']
      );
      if (!djangoData) {
        return;
      }
      const fastapiData = await fetchSwaggerJSON(
        'fastapi-swagger/openapi.json',
        authData.access,
        ['/api/results']
      );
      if (fastapiData) {
        djangoData.paths = { ...djangoData.paths, ...fastapiData.paths };
        const schemas = {
          ...fastapiData.components.schemas,
          ...djangoData.components.schemas,
        };
        const securitySchemes = {
          ...fastapiData.components.securitySchemes,
          ...djangoData.components.securitySchemes,
        };
        djangoData.components = { schemas, securitySchemes };
        setSwaggerData(djangoData);
      }
    };
    fetchData();
  }, [authData.isAuthenticated]);

  // Plugin to hide models section
  const HideModelsPlugin = () => ({
    wrapComponents: {
      Models: () => () => null,
    },
  });

  return authData.isAuthenticated == undefined ? (
    // isAuthenticated is undefined or null
    <Box data-testid="Box-36jz" sx={{ width: '100%' }}>
      <LinearProgress data-testid="LinearProgress-prpd" />
    </Box>
  ) : authData.isAuthenticated === false ? (
    // isAuthenticated is false
    <SignInDialog isAuthenticated={authData.isAuthenticated} />
  ) : !swaggerData ? (
    // From here we know that isAuthenticated is true
    <Box data-testid="Box-w1vy" sx={{ width: '100%' }}>
      <LinearProgress data-testid="LinearProgress-bj0c" />
    </Box>
  ) : authData.isDDTDAdmin || authData.isApiUser ? (
    <>
      <Box p={10} paddingX={10} alignItems="center">
        <Box
          data-testid="Box-k0n2"
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <SwaggerUI
            spec={swaggerData}
            plugins={[HideModelsPlugin]}
            requestInterceptor={(req) => {
              if (
                req.url.endsWith('/api/auth/token/') ||
                req.url.endsWith('/api/auth/refresh/') ||
                req.url.endsWith('/api/auth/token/api-key/')
              ) {
                return req;
              }
              if (tokenRef.current) {
                // Add Authorization header
                req.headers['Authorization'] = `Bearer ${tokenRef.current}`;
              }
              return req;
            }}
            responseInterceptor={(res) => {
              if (
                res.url.endsWith('/api/auth/token/') ||
                res.url.endsWith('/api/auth/refresh/') ||
                res.url.endsWith('/api/auth/token/api-key/')
              ) {
                if (res.ok) {
                  const jsonData = JSON.parse(res.data);
                  tokenRef.current = jsonData.access;
                }
              }
              return res;
            }}
            onComplete={(system: any) => setSwaggerUI(system)}
          />
        </Box>
      </Box>
    </>
  ) : null;
};

export default SwaggerPage;
